import { useState } from 'react'
import { useContactSearch } from '../lib/useContactSearch'
import { prettyPhone } from '../lib/phone'
import type { GoogleContact } from '../lib/api'

interface Props {
  value:       string
  onChange:    (v: string) => void
  onPick:      (c: GoogleContact) => void
  type?:       string
  placeholder?: string
  id?:         string
  className?:  string
}

/**
 * A text input that suggests cached Google contacts as you type — picking one
 * fills the related student fields. Used on the student form so contacts are a
 * source of names/numbers without a separate "import" step. If the cache is
 * empty it pulls from Google automatically (see useContactSearch).
 */
export function ContactSuggestInput({
  value, onChange, onPick, type = 'text', placeholder, id, className,
}: Props) {
  const [focused, setFocused] = useState(false)
  const { results, searching, pulling } = useContactSearch(value, focused)

  const matches = results.slice(0, 6)
  const open    = focused && value.trim().length > 0

  return (
    <div className="relative">
      <input
        id={id}
        type={type}
        value={value}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        onFocus={() => setFocused(true)}
        onBlur={() => setFocused(false)}
        autoComplete="off"
        className={className}
      />
      {open && (matches.length > 0 || pulling || searching) && (
        <div className="absolute z-20 mt-1 w-full border border-zinc-200 bg-white max-h-56 overflow-y-auto shadow-lg">
          {pulling ? (
            <p className="px-3 py-2 text-xs text-zinc-400">Syncing contacts from Google…</p>
          ) : matches.length === 0 && searching ? (
            <p className="px-3 py-2 text-xs text-zinc-400">Searching…</p>
          ) : (
            matches.map((c, i) => (
              <button
                key={`${c.resourceName ?? c.phone ?? ''}-${i}`}
                type="button"
                // mouseDown (not click) so selection happens before the input blurs
                onMouseDown={(e) => { e.preventDefault(); onPick(c); setFocused(false) }}
                className="flex w-full items-center justify-between gap-3 px-3 py-2 text-start text-sm hover:bg-zinc-50 border-b border-zinc-100 last:border-0"
              >
                <span className="truncate">{c.name}</span>
                <span className="text-xs text-zinc-400 shrink-0">{prettyPhone(c.phone)}</span>
              </button>
            ))
          )}
        </div>
      )}
    </div>
  )
}
